import type { PendingOperationLedger, PendingOperationLedgerReceipt } from './PendingOperationTerminalization';
import { PENDING_OPERATION_ACCOUNTED_DISABLED } from './PendingOperationTerminalization';

interface SupabaseRpcClient {
  rpc(name: string, args: Record<string, unknown>): Promise<{
    data: unknown;
    error: { message?: string } | null;
  }>;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SHA512_HEX = /^[0-9a-f]{128}$/;
const SHA512_BASE64 = /^[A-Za-z0-9+/]{85}[AQgw]==$/;
const EVENT_ID = /^[1-9][0-9]*$/;

const requireString = (value: unknown, label: string): string => {
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error(`${label} ausente no readback.`);
  }
  return value.trim();
};

const requireUuid = (value: unknown, label: string): string => {
  const normalized = requireString(value, label).toLowerCase();
  if (!UUID.test(normalized)) throw new Error(`${label} invalido no readback.`);
  return normalized;
};

const requirePayloadSha512 = (value: unknown, label: string): string => {
  const normalized = requireString(value, label);
  if (!SHA512_BASE64.test(normalized)) throw new Error(`${label} invalido no readback.`);
  return normalized;
};

const requireSha512Hex = (value: unknown, label: string): string => {
  const normalized = requireString(value, label).toLowerCase();
  if (!SHA512_HEX.test(normalized)) throw new Error(`${label} invalido no readback.`);
  return normalized;
};

const mapLedgerReceipt = (raw: unknown): PendingOperationLedgerReceipt => {
  if (Array.isArray(raw)) {
    if (raw.length !== 1) {
      throw new Error('Readback do ledger remoto com cardinalidade invalida.');
    }
    raw = raw[0];
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error('Readback do ledger remoto ausente.');
  }
  const row = raw as Record<string, unknown>;
  const eventId = requireString(row.event_id ?? row.eventId, 'eventId');
  if (!EVENT_ID.test(eventId)) {
    throw new Error('eventId do readback invalido.');
  }
  const writeSetCount = row.write_set_count ?? row.writeSetCount;
  if (!Number.isInteger(writeSetCount) || Number(writeSetCount) < 0) {
    throw new Error('writeSetCount ausente ou invalido no readback.');
  }
  return {
    eventId,
    receiptId: requireUuid(row.receipt_id ?? row.receiptId, 'receiptId'),
    pendingOperationId: requireString(row.pending_operation_id ?? row.pendingOperationId, 'pendingOperationId'),
    payloadSha512: requirePayloadSha512(row.payload_sha512 ?? row.payloadSha512, 'payloadSha512'),
    originEntityId: requireUuid(row.origin_entity_id ?? row.originEntityId, 'originEntityId'),
    successorEntityId: requireUuid(row.successor_entity_id ?? row.successorEntityId, 'successorEntityId'),
    writeSetCount: writeSetCount as number,
    writeSetDigest: requireSha512Hex(row.write_set_digest ?? row.writeSetDigest, 'writeSetDigest'),
  };
};

export const createSupabasePendingOperationLedger = (
  client: SupabaseRpcClient
): PendingOperationLedger => ({
  async accountPendingOperation(record) {
    const { data, error } = await client.rpc('app_guincho_account_pending_operation_v1', {
      p_empresa_id: record.companyId,
      p_recovery_id: record.recoveryId,
      p_receipt_id: record.receiptId,
      p_pending_operation_id: record.pendingOperationId,
      p_payload_sha512: record.payloadSha512,
      p_accounting_state: record.accountingState,
      p_origin_entity_id: record.originEntityId,
      p_successor_entity_id: record.successorEntityId,
      p_write_set_count: record.writeSetCount,
      p_write_set_digest: record.writeSetDigest,
      p_client_build: record.clientBuild,
    });
    if (error) {
      const message = error.message ?? String(error);
      if (message.includes(PENDING_OPERATION_ACCOUNTED_DISABLED)) {
        throw new Error(PENDING_OPERATION_ACCOUNTED_DISABLED);
      }
      throw new Error(message);
    }
    return mapLedgerReceipt(data);
  },
});
